<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          {{ paymentQR ? "Update Payment QR" : "Create Payment QR" }}
        </v-card-title>

        <v-card-text>
          <v-row v-if="loading">
            <v-col cols="12" class="text-center">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
              <p class="mt-3">Loading...</p>
            </v-col>
          </v-row>

          <v-row v-else>
            <!-- Saved details -->
            <v-col cols="12" v-if="paymentQR">
              <v-simple-table class="mb-2">
                <template v-slot:default>
                  <tbody>
                    <tr v-if="paymentQR.upi_id">
                      <th class="text-left" style="width: 180px">UPI ID</th>
                      <td>
                        <v-chip small color="primary" text-color="white">
                          <v-icon left x-small>mdi-cash</v-icon>
                          {{ paymentQR.upi_id }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <th class="text-left" style="width: 180px">QR Code</th>
                      <td>
                        {{ paymentQR.qr_image_path ? "Uploaded" : "Not uploaded" }}
                      </td>
                    </tr>
                    <tr v-if="paymentQR.updated_by">
                      <th class="text-left">Last Updated By</th>
                      <td>
                        {{ paymentQR.updated_by.first_name }}
                        {{ paymentQR.updated_by.last_name }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-divider class="my-3"></v-divider>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.upi_id"
                outlined
                label="UPI ID (Optional)"
                prepend-inner-icon="mdi-cash"
                placeholder="example@upi"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <label for="qr_image" class="mb-2 d-block">
                <strong
                  >QR Code Image
                  {{ !paymentQR ? "(Required)" : "(Optional)" }}</strong
                >
              </label>
              <input
                type="file"
                id="qr_image"
                ref="qr_image"
                accept="image/*"
                @change="previewImage"
              />
              <v-btn
                v-if="previewSrc"
                x-small
                class="ml-2"
                color="red"
                dark
                @click="removeImage"
              >
                Remove
              </v-btn>
            </v-col>

            <!-- Image Preview -->
            <v-col cols="12" v-if="previewSrc">
              <p class="mb-2"><strong>QR Code Preview:</strong></p>
              <v-img
                :src="previewSrc"
                alt="Payment QR Code"
                max-width="200"
                contain
              ></v-img>
            </v-col>

            <v-col cols="12">
              <v-divider class="my-3"></v-divider>
              <v-btn
                rounded
                color="primary"
                :loading="isLoading"
                :disabled="!isFormValid"
                @click="save"
              >
                {{ paymentQR ? "Update Payment QR" : "Create Payment QR" }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "PaymentQRPage",
  data() {
    return {
      paymentQR: null,
      form: {
        upi_id: "",
      },
      loading: true,
      isLoading: false,
      imagePreview: null,
      selectedFile: null,
      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },
  computed: {
    isFormValid() {
      // QR image is required, UPI ID is optional.
      // On update the existing image counts, so only a create needs a new file.
      if (!this.paymentQR) {
        return !!this.selectedFile;
      }
      return !!(this.selectedFile || this.paymentQR.qr_image_path);
    },
    previewSrc() {
      // Newly picked file wins, otherwise show the saved QR image
      if (this.imagePreview) return this.imagePreview;
      if (!this.paymentQR) return null;
      // Build from VUE_APP_MEDIA_BASE_URL, falling back to the URL from the API
      return (
        this.getImageUrl(this.paymentQR.qr_image_path) ||
        this.paymentQR.qr_image_url
      );
    },
  },
  mounted() {
    this.fetchPaymentQR();
  },
  methods: {
    async fetchPaymentQR() {
      try {
        this.loading = true;
        const response = await axios.get("/payment-qr");
        if (response.data.success && response.data.data) {
          this.paymentQR = response.data.data;
          this.form.upi_id = this.paymentQR.upi_id || "";
        }
        this.loading = false;
      } catch (error) {
        console.error("Error fetching payment QR:", error);
        this.loading = false;
      }
    },

    previewImage(event) {
      const file = event.target.files[0];
      this.selectedFile = file || null;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },

    removeImage() {
      this.$refs.qr_image.value = null;
      this.imagePreview = null;
      this.selectedFile = null;
    },

    getImageUrl(path) {
      if (!path) return null;
      // Already a full URL (e.g. when the API returns an absolute path)
      if (/^https?:\/\//i.test(path)) return path;

      const base = process.env.VUE_APP_MEDIA_BASE_URL || this.mediaUrl;
      if (!base) return null;

      // Encode each segment (filenames can contain spaces) but keep the slashes
      const encodedPath = path
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");

      return `${base.replace(/\/+$/, "")}/${encodedPath}`;
    },

    async save() {
      if (!this.isFormValid) {
        this.$toast.error("QR code image is required");
        return;
      }

      try {
        this.isLoading = true;

        const formData = new FormData();
        // UPI ID is optional - only send it when the user filled it in
        if (this.form.upi_id) {
          formData.append("upi_id", this.form.upi_id);
        }

        // Add image if selected
        if (this.selectedFile) {
          formData.append("qr_image", this.selectedFile);
        }

        let response;
        if (this.paymentQR) {
          // Update existing
          response = await axios.post(
            `/payment-qr/${this.paymentQR.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );
        } else {
          // Create new
          response = await axios.post("/payment-qr", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }

        if (response && response.data && response.data.success) {
          this.$toast.success(response.data.message);
          // Refresh data
          await this.fetchPaymentQR();
          // Clear image preview
          this.imagePreview = null;
          this.selectedFile = null;
          if (this.$refs.qr_image) {
            this.$refs.qr_image.value = null;
          }
        } else {
          this.$toast.error(
            response?.data?.message || "Failed to save payment QR",
          );
        }

        this.isLoading = false;
      } catch (error) {
        console.error("Error saving payment QR:", error);
        console.error("Error response:", error.response);
        console.error("Error request:", error.request);
        console.error("Error message:", error.message);

        this.isLoading = false;

        if (error.response) {
          // Server responded with error status
          if (error.response.status === 401) {
            this.$toast.error("Please log in to continue");
            this.$router.push("/login");
          } else if (error.response.data?.message) {
            this.$toast.error(error.response.data.message);
          } else if (error.response.data?.error) {
            this.$toast.error(error.response.data.error);
          } else if (error.response.data?.errors) {
            const firstError = Object.values(error.response.data.errors)[0];
            this.$toast.error(
              Array.isArray(firstError) ? firstError[0] : firstError,
            );
          } else {
            this.$toast.error(
              `Error: ${error.response.status} ${error.response.statusText}`,
            );
          }
        } else if (error.request) {
          // Request was made but no response received
          this.$toast.error(
            "No response from server. Please check your connection.",
          );
        } else {
          // Error setting up the request
          this.$toast.error(
            error.message || "Failed to save payment QR. Please try again.",
          );
        }
      }
    },
  },
};
</script>

<style scoped>
input[type="file"] {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.reset_image {
  margin-left: 8px;
}
</style>
